import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type CreateAxiosDefaults,
} from "axios";
import { ref } from "vue";

import { useAuthStore } from "@/modules/Authorization/store";
import { generateFingerprint } from "@/utils/browser";

export const useApi = (apiUrl?: string) => {
  const baseURL = apiUrl || (import.meta.env.VITE_APP_BASE_URL as string);
  const locale = localStorage.getItem("locale");
  const loading = ref(false);
  const authStore = useAuthStore();

  // const { handleError } = useErrorHandling();

  function $service(options?: CreateAxiosDefaults): AxiosInstance {
    const getHeaders = (): {
      Authorization: string | undefined;
      "Device-Id": string;
      "Accept-Language": string;
    } => {
      // One source for the token. The old version asked storage whether a token existed but read
      // its value from store state, so any request firing before the store was hydrated sent
      // "Bearer undefined" and came back 401 for no visible reason.
      if (!authStore.getTokens?.access) {
        authStore.initTokens();
      }
      const access = authStore.getTokens?.access;

      return {
        ...options?.headers,
        "Accept-Language": locale || "uz",
        Authorization: access ? `Bearer ${access}` : undefined,
        "Device-Id": generateFingerprint(),
      };
    };

    const instance = axios.create({
      baseURL,
      ...options,
      headers: getHeaders(),
    });
    instance.interceptors.response.use(null, async function (error: AxiosError) {
      // admission-usat-api issues one long-lived student token and has no refresh endpoint, so a
      // 401 means the session is genuinely over. The previous handler retried the identical
      // request against /Authorization/token/refresh/, a path that never existed on any backend
      // this app talked to, which turned every expiry into a silent duplicate request.
      if (error.response?.status === 401) {
        authStore.logout();
        if (window.location.pathname !== "/sign-in") {
          window.location.replace("/sign-in");
        }
      }
      return Promise.reject(error);
    });
    return instance;
  }

  function $get<T = never>(
    endpoint: string,
    options?: AxiosRequestConfig,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      loading.value = true;
      $service()
        .get(endpoint, options)
        .then((response: T | any) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  function $post<T = never, D = Record<string, any> | FormData>(
    endpoint: string,
    data?: D,
    options?: AxiosRequestConfig,
    axiosOptions?: CreateAxiosDefaults,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      $service(axiosOptions)
        .post(endpoint, data, options)
        .then((response: T | any) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  function $put<T = never, D = Record<string, any> | FormData>(
    endpoint: string,
    data?: D,
    options?: AxiosRequestConfig,
    axiosOptions?: CreateAxiosDefaults,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      $service(axiosOptions)
        .put(endpoint, data, options)
        .then((response: T | any) => {
          resolve(response.data);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  function $patch<T = never, D = Record<string, any> | FormData>(
    endpoint: string,
    data?: D,
    options?: AxiosRequestConfig,
    axiosOptions?: CreateAxiosDefaults,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      $service(axiosOptions)
        .patch(endpoint, data, options)
        .then((response: T | any) => {
          resolve(response.data);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  function $delete<T = never>(
    endpoint: string,
    options?: AxiosRequestConfig,
    axiosOptions?: CreateAxiosDefaults,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      $service(axiosOptions)
        .delete(endpoint, options)
        .then((response: T | any) => {
          resolve(response.data);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  return {
    loading,
    baseURL,
    $get,
    $post,
    $put,
    $patch,
    $delete,
  };
};
